import type { Row } from "./nav-hunks";

export interface SearchOptions {
  caseSensitive: boolean;
  regex: boolean;
  wholeWord: boolean;
}

export type CompiledMatcher =
  | { ok: true; re: RegExp }
  | { ok: false; error: string };

export interface Match {
  rowIndex: number;
  side: "left" | "right";
  start: number;
  end: number;
}

const REGEX_META = /[.*+?^${}()|[\]\\]/g;
function escapeRegExp(s: string): string {
  return s.replace(REGEX_META, "\\$&");
}

export function compileMatcher(query: string, opts: SearchOptions): CompiledMatcher {
  const base = opts.regex ? query : escapeRegExp(query);
  const wrapped = opts.wholeWord ? `\\b(?:${base})\\b` : base;
  const flags = opts.caseSensitive ? "g" : "gi";
  try {
    return { ok: true, re: new RegExp(wrapped, flags) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

export function findMatches(rows: readonly Row[], matcher: CompiledMatcher): Match[] {
  if (!matcher.ok) return [];
  // new RegExp("").source === "(?:)" in V8; the "" branch is unreachable.
  if (matcher.re.source === "(?:)") return [];
  const out: Match[] = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i] as Row;
    pushAll(out, i, "left",  r.left.text,  matcher.re);
    pushAll(out, i, "right", r.right.text, matcher.re);
  }
  return out;
}

function pushAll(
  out: Match[],
  rowIndex: number,
  side: "left" | "right",
  text: string,
  re: RegExp,
): void {
  if (text.length === 0) return;
  // matchAll uses an internal clone and does not mutate re.lastIndex.
  // compileMatcher always yields lastIndex=0, so each call starts from text[0].
  for (const m of text.matchAll(re)) {
    if (m[0].length === 0) break; // zero-width safety: skip and stop
    const start = m.index ?? 0;
    const end = start + m[0].length;
    out.push({ rowIndex, side, start, end });
  }
}
